import { CUSTOMER_STATUS } from '@monorepo/shared/schemas/common'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Archive,
  ArchiveRestore,
  Building2,
  Edit,
  MoreHorizontal,
  Trash2,
  User,
  Users,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { VirtualizedTable } from '@/shared/components/table/VirtualizedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useDeleteCustomer, useUpdateCustomer } from '../hooks/useCustomers'
import type { CustomerResponse } from '../services/customerService'

interface CustomersTableProps {
  customers: CustomerResponse[]
  isLoading?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  fetchNextPage?: () => void
  onEditCustomer?: (customer: CustomerResponse) => void
  onRowClick?: (customer: CustomerResponse) => void
}

export function CustomersTable({
  customers,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  onEditCustomer,
  onRowClick,
}: CustomersTableProps) {
  const [deleteCustomer, setDeleteCustomer] = useState<CustomerResponse | null>(
    null
  )
  const updateCustomerMutation = useUpdateCustomer()
  const deleteCustomerMutation = useDeleteCustomer()

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteCustomer) {
      try {
        await deleteCustomerMutation.mutateAsync(deleteCustomer.id)
        setDeleteCustomer(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }, [deleteCustomer, deleteCustomerMutation])

  const handleArchive = useCallback(
    async (customer: CustomerResponse) => {
      try {
        await updateCustomerMutation.mutateAsync({
          id: customer.id,
          data: { status: CUSTOMER_STATUS.inactive },
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    },
    [updateCustomerMutation]
  )

  const handleActivate = useCallback(
    async (customer: CustomerResponse) => {
      try {
        await updateCustomerMutation.mutateAsync({
          id: customer.id,
          data: { status: CUSTOMER_STATUS.active },
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    },
    [updateCustomerMutation]
  )

  const getTypeIcon = (profileKind: string | null) => {
    if (!profileKind) return null

    switch (profileKind) {
      case 'human':
        return <User className='w-3 h-3 text-blue-500' />
      case 'ai_agent':
        return <Users className='w-3 h-3 text-purple-500' />
      case 'system':
        return <Building2 className='w-3 h-3 text-gray-500' />
      default:
        return null
    }
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default'
      case 'inactive':
        return 'secondary'
      case 'suspended':
        return 'destructive'
      case 'archived':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const columns: ColumnDef<CustomerResponse>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const customer = row.original
        return (
          <div className='flex items-center gap-3'>
            <div className='w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium'>
              {customer.name.charAt(0).toUpperCase()}
            </div>
            <div className='min-w-0'>
              <div className='flex items-center gap-2'>
                <span className='font-medium truncate'>{customer.name}</span>
              </div>
              {customer.email && (
                <div className='text-sm text-muted-foreground truncate'>
                  {customer.email}
                </div>
              )}
            </div>
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'profile_kind',
      header: 'Profile Kind',
      cell: ({ row }) => {
        const customer = row.original
        const formatProfileKind = (kind: string | null) => {
          if (!kind) return '-'
          // Convert 'ai_agent' to 'AI Agent', 'human' to 'Human', etc.
          return kind
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        }
        return (
          <div className='flex items-center gap-1'>
            {getTypeIcon(customer.profile_kind)}
            <Badge variant='outline' className='text-xs'>
              {formatProfileKind(customer.profile_kind)}
            </Badge>
          </div>
        )
      },
      size: 300,
    },

    {
      accessorKey: 'organization',
      header: 'Organization',
      cell: ({ row }) => {
        const customer = row.original
        return (
          <div className='text-sm'>
            {customer.organization_id ? (
              <div className='flex items-center gap-1'>
                <Building2 className='w-3 h-3 text-muted-foreground' />
                <span className='truncate'>
                  {customer.organization?.name || 'Unknown'}
                </span>
              </div>
            ) : (
              <span className='text-muted-foreground'>-</span>
            )}
          </div>
        )
      },
      size: 400,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const customer = row.original
        return (
          <Badge variant={getStatusVariant(customer.status)}>
            {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
          </Badge>
        )
      },
      size: 200,
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      cell: ({ row }) => {
        const customer = row.original
        return (
          <div className='text-sm text-muted-foreground'>
            {formatDate(customer.updated_at)}
          </div>
        )
      },
      size: 180,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const customer = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='h-8 w-8 p-0'
                onClick={e => e.stopPropagation()}
              >
                <span className='sr-only'>Open menu</span>
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {/* Edit - available for all statuses */}
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  onEditCustomer?.(customer)
                }}
              >
                <Edit className='mr-2 h-4 w-4' />
                Edit
              </DropdownMenuItem>

              {/* Status-based actions */}
              {customer.status === CUSTOMER_STATUS.active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleArchive(customer)
                  }}
                >
                  <Archive className='mr-2 h-4 w-4' />
                  Deactivate
                </DropdownMenuItem>
              )}

              {customer.status === CUSTOMER_STATUS.inactive && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleActivate(customer)
                  }}
                >
                  <ArchiveRestore className='mr-2 h-4 w-4' />
                  Activate
                </DropdownMenuItem>
              )}

              {customer.status === CUSTOMER_STATUS.suspended && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleActivate(customer)
                  }}
                >
                  <ArchiveRestore className='mr-2 h-4 w-4' />
                  Activate
                </DropdownMenuItem>
              )}

              {/* Delete - available for all statuses */}
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  setDeleteCustomer(customer)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  return (
    <div className='space-y-4'>
      <VirtualizedTable
        data={customers}
        columns={columns}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onRowClick={onRowClick}
        containerHeight='calc(100vh - 350px)'
      />

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        open={!!deleteCustomer}
        onOpenChange={open => !open && setDeleteCustomer(null)}
        title='Delete Customer'
        description={`Are you sure you want to delete "${deleteCustomer?.name}"? This action cannot be undone and will permanently remove the customer and all associated data.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteCustomerMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteCustomer(null)}
      />
    </div>
  )
}
